import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './NumberInput.scss';
import InputControl from '../InputControl/InputControl';
import {
  UNIT_DECORATORS,
  getRawValue,
  formatValue,
  preciseRound,
} from '../../utils/format/number';
import CaretManager from '../../utils/fields/CaretManager';
import useIsMounted from '../../utils/useIsMounted';

const VALID_KEYS_REGEX = /[\d|.]/;
const UNIT_VALUE_MAX_LENGTH = 15;

const nthMatch = (string, pattern, n) => {
  let nthIndex = 0;
  return Array.from(string).findIndex((char) => {
    nthIndex += pattern.test(char);
    return nthIndex === n;
  });
};

/**
 For further guidance on the NumberInput component, please visit the
   <a target="_blank" href="http://hqcontent.edwardjones.com/GUI/guisite/axiom-ds/components/form/text-input" class="axiom-link"> AXIOM</a>
    documentation site.
   <br/>
   <br/>
 Sentence style, no colon at the end.

####Required fields

- If all fields are required, state at the top of the form “All required.”
- If only some fields are required, add an asterisk before the label and a note at the top of the
form “* Required.”
- If most of the fields are required but a few are optional, include “(optional)” after the field
label.
* */
const NumberInput = ({
  id,
  classes: modifierClasses,
  label,
  type,
  name,
  value,
  precision,
  labelModifier,
  disabled,
  readOnly,
  error,
  microcopy,
  onBlur,
  onChange,
  inputRef: inputRefProp,
  hideLabel,
  rounded,
  formatted: formattedProp,
  attributes,
  condensed,
}) => {
  const internalInputRef = useRef();
  const isMounted = useIsMounted();
  const inputRef = inputRefProp || internalInputRef;

  const formatted =
    typeof formattedProp === 'undefined'
      ? type !== 'numeric'
      : formattedProp;

  const getDefaultValueSegments = () => {
    const rawValue =
      value === null ? '' : getRawValue(value.toString());
    const roundedValue = preciseRound(rawValue, precision);

    const textValue = rounded
      ? roundedValue.toFixed(precision)
      : roundedValue.toString();

    return textValue.split('.', 2);
  };

  const [valueSegments, setValueSegments] = useState(
    getDefaultValueSegments()
  );

  const [caretPosition, setCaretPosition] = useState(null);

  let inputMode = type;
  if (['currency', 'percent'].includes(type)) {
    inputMode = 'decimal';
  }

  const getValue = () => getRawValue(valueSegments.join('.'));

  const getFormattedValue = (
    segments,
    formatType,
    showPrecision = false
  ) => {
    let formattedValueSegments = [...segments];

    if (formatted) {
      const textValue = formattedValueSegments.join('.');

      if (textValue !== '.') {
        const decimalValue = getRawValue(segments[1]);
        const decimalText = Number.isNaN(decimalValue)
          ? ''
          : String(segments[1].replace(/[^\d]/g, ''));
        const displayPrecision = showPrecision
          ? precision
          : decimalText.length;

        const rawValue = getRawValue(textValue);
        formattedValueSegments = formatValue(
          rawValue,
          formatType,
          displayPrecision
        ).split('.', 2);
      }
    }

    const formattedValue = formattedValueSegments.join('.');

    if (!VALID_KEYS_REGEX.test(formattedValue)) {
      return '';
    }

    if (segments[0] === '') {
      if (type === 'currency') {
        formattedValueSegments[0] = '$';
      } else {
        formattedValueSegments[0] = '';
      }
    }

    if (segments[1] === '') {
      if (type === 'percent') {
        formattedValueSegments[0] = formattedValueSegments[0].replace(
          '%',
          ''
        );
        formattedValueSegments[1] = '%';
      } else {
        formattedValueSegments[1] = '';
      }
    }

    return formattedValueSegments.join('.');
  };

  useEffect(() => {
    if (isMounted) {
      setValueSegments(getDefaultValueSegments());
    }
  }, [value]);

  useEffect(() => {
    if (isMounted) {
      onChange(getFormattedValue(valueSegments, type));
    }
  }, [valueSegments]);

  useEffect(() => {
    if (caretPosition !== null) {
      /* istanbul ignore next */
      window.requestAnimationFrame(() =>
        inputRef.current.setSelectionRange(
          caretPosition.start,
          caretPosition.end
        )
      );
    }
  }, [caretPosition]);

  const handleDataRemoval = (editorCaret, pressedKey) => {
    const updatedValueSegments = [...valueSegments];

    const currentValue = getFormattedValue(
      updatedValueSegments,
      type
    );
    const currentCommaMatch = currentValue.match(/,/g);
    const currentCommaCount =
      currentCommaMatch === null ? 0 : currentCommaMatch.length;

    const newValue = {
      start: currentValue.substring(0, editorCaret.start),
      end: currentValue.substring(editorCaret.end),
      highlighted: currentValue
        .substring(editorCaret.start, editorCaret.end)
        .replace(new RegExp('[^/]', 'g'), ''),
    };

    const isDeletingBlock = editorCaret.length > 0;

    if (!isDeletingBlock) {
      if (pressedKey === 'Backspace') {
        const isDeletingDecorator = UNIT_DECORATORS.includes(
          newValue.start.substr(-1)
        );

        const deleteBy = 1 + isDeletingDecorator;
        newValue.start = newValue.start.slice(0, -1 * deleteBy);
        editorCaret.moveBy(-1 * deleteBy);
      }

      if (pressedKey === 'Delete') {
        const isDeletingDecorator = UNIT_DECORATORS.includes(
          newValue.end.substr(0, 1)
        );

        const deleteBy = 1 + isDeletingDecorator;
        newValue.end = newValue.end.slice(deleteBy);
      }
    }

    const updatedValue = `${newValue.start}${newValue.highlighted}${newValue.end}`;

    let updatedFormattedValue = getFormattedValue(
      String(getRawValue(updatedValue)).split('.', 2),
      type
    );

    if (updatedValue.substr(-1) === '.') {
      updatedFormattedValue += '.';
    }

    const updatedCommaMatch = updatedFormattedValue.match(/,/g);
    const updatedCommaCount =
      updatedCommaMatch === null ? 0 : updatedCommaMatch.length;

    editorCaret.moveBy(updatedCommaCount - currentCommaCount);
    editorCaret.collapse();

    const updatedSegmentValue = updatedValue
      .split('.', 2)
      .map((segment) => {
        const segmentValue = getRawValue(segment);
        return Number.isNaN(segmentValue) ? '' : segment;
      });

    setValueSegments(updatedSegmentValue);
    setCaretPosition(editorCaret);
  };

  const handleChange = (event) => {
    const domInput = event.target;
    const editorCaret = new CaretManager(domInput);

    // Allow user to enter '-' only one time and as a first character
    const isNegativeNumber =
      domInput.value.length > 0 && domInput.value[0] === '-';

    const updatedValue =
      (isNegativeNumber ? '-' : '') +
      domInput.value.replace(/[^\d|.]/g, '');

    const updatedValueSegments = updatedValue.split('.', 2);

    if (
      formatted &&
      typeof updatedValueSegments[0] !== 'undefined' &&
      updatedValueSegments[0] !== ''
    ) {
      updatedValueSegments[0] = getRawValue(updatedValueSegments[0]);

      if (Number.isNaN(updatedValueSegments[0])) {
        updatedValueSegments[0] = '';
      }
    }

    setValueSegments(updatedValueSegments);

    const prevCharacterCount = Array.from(
      domInput.value.substring(0, editorCaret.start)
    ).filter((char) => VALID_KEYS_REGEX.test(char)).length;

    const formattedValue = getFormattedValue(
      updatedValueSegments,
      type
    );
    const updatedPosition =
      nthMatch(formattedValue, VALID_KEYS_REGEX, prevCharacterCount) +
      1;

    editorCaret.jumpTo(updatedPosition);
    setCaretPosition(editorCaret);
  };

  const handleBlur = () => {
    let rawValue = getValue();
    let updatedSegmentValue = [...valueSegments];

    if (rounded) {
      rawValue = preciseRound(rawValue, precision);

      updatedSegmentValue = rawValue.toFixed(precision).split('.', 2);

      setValueSegments(updatedSegmentValue);
    }

    if (onBlur) {
      onBlur(getFormattedValue(updatedSegmentValue, type, true));
    }
  };

  const handleKeyPress = (event) => {
    const domInput = event.target;
    const pressedKey = event.key;

    const isNumeric = /^[\d|-]$/.test(pressedKey);
    const isDecimalKey = pressedKey === '.';
    const isDeleteKey = ['Backspace', 'Delete'].includes(pressedKey);

    const editorCaret = new CaretManager(domInput);
    let suppressDefaultEvent = false;

    if (isNumeric) {
      const val = domInput.value;
      const nonHighlightedValue =
        val.substring(0, editorCaret.start) +
        val.substring(editorCaret.end);

      const numericVal = nonHighlightedValue.replace('%', '') || '';
      const valSegments = numericVal.split('.') || [];
      const unitValue = valSegments[0].replace(/[^\d|.]/g, '');
      const precisionValue = valSegments[1] || '';

      const decimalPosition = numericVal.indexOf('.');
      if (
        precision &&
        precisionValue.length >= precision &&
        decimalPosition < editorCaret.start
      ) {
        suppressDefaultEvent = true;
      }

      if (formatted && unitValue.length >= UNIT_VALUE_MAX_LENGTH) {
        suppressDefaultEvent = true;
      }
    }

    if (isDecimalKey) {
      if (!precision || domInput.value.includes('.')) {
        suppressDefaultEvent = true;
      }
    }

    if (isDeleteKey) {
      handleDataRemoval(editorCaret, pressedKey);
      suppressDefaultEvent = true;
    }

    if (suppressDefaultEvent) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  const classes = [
    'axiom-number-input--input-wrap',
    'input-control--input--wrap',
  ];

  if (valueSegments.length) {
    classes.push(
      'axiom-number-input--input-wrap--has-value input-control--input--wrap--has-value'
    );
  }

  if (condensed) {
    classes.push(
      'axiom-number-input--input-wrap--condensed input-control--input--wrap--condensed'
    );
  }

  const formattedValue = getFormattedValue(valueSegments, type);

  const normalizedPropClasses =
    modifierClasses instanceof Array
      ? modifierClasses
      : [modifierClasses];

  const controllerClasses = [
    'axiom-number-input',
    ...normalizedPropClasses,
  ];

  return (
    <InputControl
      id={id}
      classes={controllerClasses}
      label={label}
      labelModifier={labelModifier}
      error={error}
      microcopy={microcopy}
      hideLabel={hideLabel}
    >
      <div data-testid='wrap' className={classes.join(' ')}>
        <input
          id={id}
          data-testid='input'
          aria-required={labelModifier === 'required'}
          aria-invalid={!!error}
          aria-describedby={
            error || microcopy ? `${id}_messages` : null
          }
          name={name}
          value={value === null ? '' : formattedValue}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
          onBlur={handleBlur}
          disabled={disabled}
          readOnly={readOnly}
          inputMode={inputMode}
          className='axiom-number-input--input'
          ref={inputRef}
          {...attributes}
        />
      </div>
    </InputControl>
  );
};

NumberInput.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['numeric', 'currency', 'percent']),
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  precision: PropTypes.number.isRequired,
  labelModifier: PropTypes.oneOf(['required', 'optional']),
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  error: PropTypes.string,
  microcopy: PropTypes.string,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  hideLabel: PropTypes.bool,
  rounded: PropTypes.bool.isRequired,
  formatted: PropTypes.bool,
  attributes: PropTypes.objectOf(PropTypes.any),
  condensed: PropTypes.bool,
  classes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};

NumberInput.defaultProps = {
  type: 'numeric',
  value: '',
  labelModifier: undefined,
  disabled: false,
  readOnly: false,
  error: undefined,
  microcopy: undefined,
  inputRef: undefined,
  onBlur: undefined,
  hideLabel: false,
  formatted: undefined,
  attributes: {},
  condensed: false,
  classes: [],
};

export default NumberInput;
